import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Plane, Users, HeartPulse, FileText, Mail, Hotel, Stethoscope,
  Bell, Info, Activity, LogOut, Search, ChevronDown, User as UserIcon, Menu, X, ShieldCheck,
  Inbox, Send, Building2, Wallet, Boxes, Network, KeyRound
} from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const nav = [
  { section: "UTAMA", module: null, items: [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-link-dashboard" },
  ]},
  { section: "PERLENGKAPAN", items: [
    { to: "/perlengkapan/pilot", label: "Pilot", icon: Plane, testid: "sidebar-link-pilot", module: "pilot" },
    { to: "/perlengkapan/fa", label: "Flight Attendant", icon: Users, testid: "sidebar-link-fa", module: "fa" },
    { to: "/perlengkapan/medis", label: "Medical", icon: HeartPulse, testid: "sidebar-link-medical", module: "medical" },
  ]},
  { section: "DOKUMEN", items: [
    { to: "/dokumen/1", label: "Level 1", icon: FileText, testid: "sidebar-link-doc-1", module: "document" },
    { to: "/dokumen/2", label: "Level 2", icon: FileText, testid: "sidebar-link-doc-2", module: "document" },
    { to: "/dokumen/3", label: "Level 3", icon: FileText, testid: "sidebar-link-doc-3", module: "document" },
    { to: "/dokumen/4", label: "Level 4", icon: FileText, testid: "sidebar-link-doc-4", module: "document" },
  ]},
  { section: "HOTEL", items: [
    { to: "/hotel/pks", label: "PKS Hotel", icon: ShieldCheck, testid: "sidebar-link-pks", module: "hotel" },
    { to: "/hotel/list", label: "List Hotel", icon: Hotel, testid: "sidebar-link-hotels", module: "hotel" },
  ]},
  { section: "PRE-FLIGHT MEDICAL", items: [
    { to: "/preflight/vendor", label: "Vendor", icon: ShieldCheck, testid: "sidebar-link-preflight-vendor", module: "preflight" },
    { to: "/preflight/laporan", label: "Report", icon: Stethoscope, testid: "sidebar-link-preflight-laporan", module: "preflight" },
  ]},
  { section: "NOTICE", items: [
    { to: "/notice/of", label: "OF", icon: Bell, testid: "sidebar-link-of-notice", module: "notice_of" },
    { to: "/notice/ofc", label: "OFC", icon: Bell, testid: "sidebar-link-ofc-notice", module: "notice_ofc" },
  ]},
  { section: "INFO", items: [
    { to: "/info/of", label: "OF", icon: Info, testid: "sidebar-link-of-info", module: "info_of" },
    { to: "/info/ofa", label: "OFA", icon: Info, testid: "sidebar-link-ofa-info", module: "info_ofa" },
    { to: "/info/ofb", label: "OFB", icon: Info, testid: "sidebar-link-ofb-info", module: "info_ofb" },
    { to: "/info/ofc", label: "OFC", icon: Info, testid: "sidebar-link-ofc-info", module: "info_ofc" },
    { to: "/info/oft", label: "OFT", icon: Info, testid: "sidebar-link-oft-info", module: "info_oft" },
  ]},
  { section: "JKTOF", items: [
    { to: "/jktof", label: "Overview", icon: Building2, testid: "sidebar-link-jktof", modules: ["jktof_of","jktof_ofc","jktof_cash"] },
    { to: "/jktof/inventaris-of", label: "Inventaris OF", icon: Boxes, testid: "sidebar-link-jktof-of", module: "jktof_of" },
    { to: "/jktof/inventaris-ofc", label: "Inventaris OFC", icon: Boxes, testid: "sidebar-link-jktof-ofc", module: "jktof_ofc" },
    { to: "/jktof/kas", label: "Uang Kas", icon: Wallet, testid: "sidebar-link-jktof-kas", module: "jktof_cash" },
    { subheader: "Surat", modules: ["surat_pilot","surat_fa"] },
    { to: "/jktof/surat/masuk", label: "Surat Masuk", icon: Inbox, testid: "sidebar-link-surat-masuk", indent: true, modules: ["surat_pilot","surat_fa"] },
    { to: "/jktof/surat/keluar", label: "Surat Keluar", icon: Send, testid: "sidebar-link-surat-keluar", indent: true, modules: ["surat_pilot","surat_fa"] },
  ]},
  { section: "SYSTEM", items: [
    { to: "/system/users", label: "User Management", icon: UserIcon, testid: "sidebar-link-users", adminOnly: true },
    { to: "/system/organization", label: "Struktur Organisasi", icon: Network, testid: "sidebar-link-org", adminOnly: true },
    { to: "/audit-log", label: "Activity Log", icon: Activity, testid: "sidebar-link-audit", adminOnly: true },
  ]},
];

const roleLabel = {
  administrator: "Administrator",
  admin_fog_head: "Admin Flight Operation Group Head",
  fog_head: "Flight Operation Group Head",
  admin_chief_pilot: "Admin Chief Pilot",
  admin_chief_pilot_airbus: "Admin Chief Pilot",
  admin_chief_fa: "Admin Chief Flight Attendant",
  pilot_management: "Pilot Management",
  fa_management: "FA Management",
  flight_operation_support: "Flight Operation Support",
  medical: "Medical",
  document_controller: "Document Controller",
  jktof: "JKTOF",
  jktdo: "JKTDO (Read Only)",
  viewer: "Viewer (Read Only)",
};

const Sidebar = ({ onNavigate }) => {
  const { hasModule, hasAnyModule, isAdmin } = useAuth();
  const filterItem = (it) => {
    if (it.adminOnly && !isAdmin()) return false;
    if (it.modules) return hasAnyModule(it.modules);
    if (it.module && !hasModule(it.module)) return false;
    return true;
  };
  const filterSubHeader = (it) => {
    if (it.module) return hasModule(it.module);
    if (it.modules) return hasAnyModule(it.modules);
    return true;
  };
  const visibleNav = nav
    .map((sec) => ({ ...sec, items: sec.items.filter((it) => it.to ? filterItem(it) : filterSubHeader(it)) }))
    .filter((sec) => sec.items.some((it) => it.to));

  return (
  <aside className="fodm-sidebar-gradient h-full w-64 flex flex-col text-white overflow-y-auto sidebar-scroll" data-testid="app-sidebar">
    <div className="px-5 py-6 border-b border-white/10">
      <div className="flex items-center gap-3">
        <BrandLogo size={40} />
        <div>
          <div className="font-bold text-base leading-tight tracking-tight">E-System JKTOFQG</div>
          <div className="text-[11px] text-white/70">Flight Operation Group</div>
        </div>
      </div>
    </div>
    <nav className="flex-1 px-3 py-4 space-y-5">
      {visibleNav.map((sec) => (
        <div key={sec.section}>
          <div className="px-3 mb-2 text-[10px] font-semibold tracking-widest text-white/50">{sec.section}</div>
          <div className="space-y-1">
            {sec.items.map((it, idx) => {
              if (it.subheader) return (
                <div key={`sh-${idx}`} className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#FFC72C]/90">{it.subheader}</div>
              );
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  onClick={onNavigate}
                  data-testid={it.testid}
                  className={({ isActive }) =>
                    `flex items-center justify-between gap-3 ${it.indent ? "ml-3 pl-3 border-l border-white/15" : ""} px-3 py-2 rounded-lg text-sm transition-all ${
                      isActive ? "bg-[#FFC72C] text-[#083B1E] font-semibold shadow-md" : "text-white/85 hover:bg-white/10"
                    }`
                  }
                >
                  <span className="flex items-center gap-3">
                    <it.icon className="w-4 h-4" />
                    <span>{it.label}</span>
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
    <div className="px-5 py-4 border-t border-white/10 text-[11px] text-white/60">
      v1.0 · E-System JKTOFQG
    </div>
  </aside>
  );
};

const TopBar = ({ onOpenMobile }) => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const crumb = loc.pathname.replace("/", "").replace("/", " › ") || "dashboard";

  return (
    <header className="sticky top-0 z-20 bg-white border-b border-slate-200" data-testid="app-topbar">
      <div className="flex items-center justify-between h-16 px-4 lg:px-8 gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <button className="lg:hidden p-2 rounded-lg hover:bg-slate-100" onClick={onOpenMobile} data-testid="mobile-menu-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden md:flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <span>Portal</span><span>›</span><span className="text-[#00A651] font-semibold">{crumb}</span>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-2 flex-1 max-w-md relative">
          <Search className="w-4 h-4 absolute left-3 text-slate-400" />
          <Input placeholder="Cari dokumen, perlengkapan, hotel..." className="pl-9 bg-slate-50 border-slate-200" data-testid="global-search" />
        </div>
        <div className="flex items-center gap-2">
          <button className="relative p-2 rounded-lg hover:bg-slate-100" data-testid="notif-btn">
            <Bell className="w-5 h-5 text-slate-600" />
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#FFC72C]"></span>
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-lg hover:bg-slate-100" data-testid="user-profile-btn">
                <Avatar className="w-8 h-8"><AvatarFallback className="bg-[#00A651] text-white text-xs font-bold">{(user?.name || "?").slice(0,2).toUpperCase()}</AvatarFallback></Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-xs font-semibold text-slate-800 leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-slate-500">{roleLabel[user?.role]}</div>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="text-xs text-slate-500">Masuk sebagai</div>
                <div className="font-semibold text-slate-800 text-sm">{user?.email}</div>
                {user?.department && <div className="text-[10px] text-slate-500 mt-0.5">Dept: {user.department}</div>}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem><UserIcon className="w-4 h-4 mr-2" /> Profil</DropdownMenuItem>
              <DropdownMenuItem className="text-xs"><Badge variant="outline" className="text-[10px]">{roleLabel[user?.role]}</Badge></DropdownMenuItem>
              <DropdownMenuSeparator />
              <ChangePasswordDialog trigger={
                <DropdownMenuItem onSelect={(e)=>e.preventDefault()} data-testid="change-password-btn"><KeyRound className="w-4 h-4 mr-2"/> Ubah Password</DropdownMenuItem>
              }/>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => { logout(); nav("/login"); }} data-testid="logout-btn" className="text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Keluar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default function AppLayout({ children }) {
  const [openMobile, setOpenMobile] = React.useState(false);
  return (
    <div className="min-h-screen flex bg-[#F4F6F8]">
      <div className="hidden lg:block fixed inset-y-0 left-0"><Sidebar /></div>
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="p-0 w-64 bg-[#083B1E] border-none">
          <Sidebar onNavigate={() => setOpenMobile(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex-1 lg:ml-64 flex flex-col min-w-0">
        <TopBar onOpenMobile={() => setOpenMobile(true)} />
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
